import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Chip, FormControl, InputLabel, MenuItem, Select, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs,
} from '@mui/material';
import { format } from 'date-fns';
import { AppPage, AppSection, EmptyState, FigureRow } from '../../components/app/AppPage';
import taxService from '../../services/taxService';
import { companyService } from '../../services/companyService';
import { logger } from '../../utils/logger';

const TABS = [
  { value: '', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'completed', label: 'Completed' },
  { value: 'attention', label: 'Needs attention' },
];

const STATUS_LABEL = {
  DRAFT: ['Draft', 'default'],
  PENDING: ['Due', 'warning'],
  PROCESSING: ['Processing', 'info'],
  SUBMITTED: ['Submitted', 'info'],
  COMPLETED: ['Completed', 'success'],
  FAILED: ['Failed', 'error'],
  CANCELLED: ['Cancelled', 'default'],
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return format(new Date(v), 'MMM d, yyyy'); } catch (e) { return v; }
};
const fmtMoney = (v) => (v === null || v === undefined ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v));

const StatusChip = ({ status }) => {
  const [label, color] = STATUS_LABEL[(status || '').toUpperCase()] || [status || 'Unknown', 'default'];
  return <Chip size="small" variant="outlined" color={color} label={label} />;
};

const TaxFilingManager = () => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [status, setStatus] = useState('');
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    companyService.getCompanies()
      .then((data) => {
        if (!live) return;
        const list = Array.isArray(data) ? data : [];
        setCompanies(list);
        if (list.length) setCompanyId(String(list[0].id));
        else setLoading(false);
      })
      .catch((err) => {
        logger.error('Failed to load companies:', err);
        if (live) { setError('We could not load your companies. Refresh the page to try again.'); setLoading(false); }
      });
    return () => { live = false; };
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      setFilings(await taxService.getTaxFilings(parseInt(companyId, 10), status || undefined));
    } catch (err) {
      logger.error('Failed to load tax filings:', err);
      setError('We could not load filings for this company. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [companyId, status]);

  useEffect(() => { load(); }, [load]);

  const figures = useMemo(() => {
    const now = new Date();
    const open = filings.filter((f) => ['DRAFT', 'PENDING'].includes((f.status || '').toUpperCase()));
    const overdue = open.filter((f) => f.due_date && new Date(f.due_date) < now);
    const liability = open.reduce((sum, f) => sum + (f.tax_liability_amount || 0), 0);
    return [
      { label: 'Filings shown', value: filings.length },
      { label: 'Upcoming', value: open.length },
      { label: 'Past due', value: overdue.length, tone: overdue.length ? 'error' : undefined },
      { label: 'Tax due on upcoming', value: fmtMoney(liability) },
    ];
  }, [filings]);

  const companyName = companies.find((c) => String(c.id) === companyId)?.name;

  return (
    <AppPage
      width="wide"
      title="Tax filings"
      description="Every sales tax return recorded for your company, by state and period, from due through completed."
      meta={companyName ? `Company: ${companyName}` : undefined}
      actions={companies.length > 1 ? (
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="filings-company">Company</InputLabel>
          <Select labelId="filings-company" label="Company" value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}>
            {companies.map((c) => <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
      ) : undefined}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!error && !loading && companies.length === 0 ? (
        <EmptyState
          title="Add your company first"
          body="Filings are kept per company. Add your business details and the states you sell into."
          action={{ label: 'Add company', to: '/companies/new' }}
        />
      ) : (
        <>
          <FigureRow items={figures} />
          <AppSection
            title="Filings"
            flat
            action={<Button size="small" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</Button>}
          >
            <Tabs value={status} onChange={(_, v) => setStatus(v)} variant="scrollable" sx={{ mb: 2 }}>
              {TABS.map((t) => <Tab key={t.value || 'all'} value={t.value} label={t.label} />)}
            </Tabs>
            {loading ? (
              <div className="gt-skeleton" style={{ height: 160 }} aria-busy="true" aria-label="Loading filings" />
            ) : !error && filings.length === 0 ? (
              <EmptyState
                title={status ? 'No filings in this view' : 'No filings recorded yet'}
                body={status
                  ? 'Switch to All to see every filing for this company.'
                  : 'A filing is due once you are registered in a state. Check where you have nexus, register there, and each filing period will be tracked here.'}
                action={status ? { label: 'Show all', onClick: () => setStatus('') } : { label: 'Check nexus', to: '/nexus/dashboard' }}
                secondary={status ? undefined : { label: 'Registrations', to: '/tax/registration' }}
              />
            ) : filings.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>State</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Due</TableCell>
                      <TableCell align="right">Taxable sales</TableCell>
                      <TableCell align="right">Tax due</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Confirmation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filings.map((f) => {
                      const overdue = f.due_date && new Date(f.due_date) < new Date()
                        && ['DRAFT', 'PENDING'].includes((f.status || '').toUpperCase());
                      return (
                        <TableRow key={f.id} hover>
                          <TableCell>{f.jurisdiction_name || f.jurisdiction || '—'}</TableCell>
                          <TableCell title={f.period_start ? `${fmtDate(f.period_start)} to ${fmtDate(f.period_end)}` : undefined}>
                            {f.filing_period}
                          </TableCell>
                          <TableCell sx={overdue ? { color: 'error.main', fontWeight: 600 } : undefined}>
                            {fmtDate(f.due_date)}{overdue ? ' · past due' : ''}
                          </TableCell>
                          <TableCell align="right" className="num">{fmtMoney(f.taxable_amount)}</TableCell>
                          <TableCell align="right" className="num">{fmtMoney(f.tax_liability_amount)}</TableCell>
                          <TableCell><StatusChip status={f.status} /></TableCell>
                          <TableCell>{f.confirmation_number || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </AppSection>
        </>
      )}
    </AppPage>
  );
};

export default TaxFilingManager;
